import { useCallback, useEffect, useMemo, useRef } from "react";
import { SERVICE_REQUEST_TYPE, serviceRequestType } from ".";
import { datetime } from "../../../../util/library/datetime2";
import { selectOneSetting } from "../../../../util/store/selectors";
import { getStore } from "../../../../util/store/store";
import { aWeekMillisecond, CALENDAR_VIEW, OP_STATUS, SHORT_DATETIME_FORMAT, VIEW_SMOOTH } from "../../../../util/varibles/constants";
import { getElById, getMondayByKey } from "../../../../util/varibles/global";
import { IServiceRequest } from "../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../util/varibles/message";
import { PlanSetting } from "../../../../util/varibles/userSetting";
import { CALENDAR_ID, selectPlan } from "../../constants";
import styles from './style.module.scss';

export default function OpStatus({ data }: { data: NonNullable<IServiceRequest['operations']>[number]; }) {
    const { status, id, operation_code, est_start_time, est_finish_time } = data;
    const cancelHighlightRef = useRef<() => void>();
    useEffect(() => () => cancelHighlightRef.current?.(), []);

    const { name, background } = useMemo(() => {
        switch (status) {
            case OP_STATUS.PROCESSING:
                return serviceRequestType[SERVICE_REQUEST_TYPE.ONGOING];

            case OP_STATUS.FINISHED:
                return serviceRequestType[SERVICE_REQUEST_TYPE.FINISHED];

            default:
                return serviceRequestType[SERVICE_REQUEST_TYPE.PLANNED];
        }
    }, [status])

    const handleClick = useCallback((id: string) => {
        const state = getStore();
        const ops = selectPlan.ops(state);
        const weeks = selectPlan.weeks(state);
        const calendarStartTime = getMondayByKey(weeks[0]).time;
        const calendarFinishTime = getMondayByKey(weeks[weeks.length - 1]).time + aWeekMillisecond - 1;

        if (!ops[id]) {
            if (est_start_time && est_finish_time) {
                if (est_finish_time <= calendarStartTime) {
                    notify.warn(NotifyCode.W14)([datetime(est_start_time).format(SHORT_DATETIME_FORMAT)]);
                    return;
                } else if (est_start_time >= calendarFinishTime) {
                    notify.warn(NotifyCode.W15)([datetime(est_start_time).format(SHORT_DATETIME_FORMAT)]);
                    return;
                }
            }
            notify.warn(NotifyCode.W13)();
            return;
        }

        const vesselId = ops[id].vessel.id;
        const vessels = selectPlan.vessels(state);
        const { isShow, name } = vessels.find(item => item.id === vesselId) || {};

        if (!isShow) {
            notify.warn(NotifyCode.W10)([name]);
            return
        }

        const el = getElById(id);
        if (el) {
            cancelHighlightRef.current?.();

            const mode = selectOneSetting(PlanSetting.CALENDAR_VIEW)(state)
            const previousScrollMarginTop = el.style.scrollMarginTop;
            const previousTransformOrigin = el.style.transformOrigin;
            const calendarEl = getElById(CALENDAR_ID);

            if (mode === CALENDAR_VIEW.HORIZONTAL) {
                const vesselEl = calendarEl?.querySelector<HTMLElement>(`[data-scroll-vessel-id="${CSS.escape(vesselId)}"]`);
                if (vesselEl) {
                    // Align the vessel row below the sticky week header.
                    const offset = el.getBoundingClientRect().top - vesselEl.getBoundingClientRect().top;
                    el.style.scrollMarginTop = `${Math.max(0, offset) + 45}px`;
                }
            }

            if (mode === CALENDAR_VIEW.VERTICAL && calendarEl) {
                const vesselEl = el.closest<HTMLElement>('[data-rule="vessel"]');
                const calendarRect = calendarEl.getBoundingClientRect();
                const operationRect = el.getBoundingClientRect();
                const vesselRect = vesselEl?.getBoundingClientRect() || operationRect;

                calendarEl.scrollTo({
                    behavior: VIEW_SMOOTH.behavior,
                    top: calendarEl.scrollTop + operationRect.top - calendarRect.top - calendarEl.clientTop - 50,
                    left: calendarEl.scrollLeft + vesselRect.left - calendarRect.left - calendarEl.clientLeft - 110,
                });
            } else {
                el.scrollIntoView(VIEW_SMOOTH);
            }

            el.style.scrollMarginTop = previousScrollMarginTop;

            let frame = 0;
            let resetTimer: ReturnType<typeof setTimeout> | undefined;
            let cleanupTimer: ReturnType<typeof setTimeout> | undefined;
            let highlighted = false;
            let previousZIndex = el.style.zIndex;

            const resetHighlight = () => {
                el.style.transform = "";
                el.style.transformOrigin = previousTransformOrigin;
                el.style.boxShadow = "";
                el.style.borderColor = "";
                el.style.zIndex = previousZIndex;
            };

            const highlight = () => {
                if (highlighted) return;

                cancelAnimationFrame(frame);
                calendarEl?.removeEventListener('scrollend', highlight);
                if (!el.isConnected) return;

                highlighted = true;
                previousZIndex = el.style.zIndex;

                const zIndex = parseInt(getComputedStyle(el).zIndex, 10) || 0;
                el.style.zIndex = `${zIndex + 1}`;

                if (mode === CALENDAR_VIEW.VERTICAL) {
                    el.style.transformOrigin = 'top center';
                }

                el.style.transform = mode === CALENDAR_VIEW.VERTICAL ? 'scale(1.2)' : 'scale(1.3)';
                el.style.boxShadow = '0 0 0 2px var(--colorActive)';
                el.style.borderColor = 'var(--colorActive)';

                resetTimer = setTimeout(() => {
                    el.style.transform = 'scale(1)';
                    // Keep the origin and stacking order fixed throughout the 300ms shrink transition.
                    cleanupTimer = setTimeout(resetHighlight, 320);
                }, 350);
            };

            cancelHighlightRef.current = () => {
                cancelAnimationFrame(frame);
                calendarEl?.removeEventListener('scrollend', highlight);
                clearTimeout(resetTimer);
                clearTimeout(cleanupTimer);
                if (highlighted) resetHighlight();
            };

            if (calendarEl) {
                calendarEl.addEventListener('scrollend', highlight, { once: true });

                let left = calendarEl.scrollLeft;
                let top = calendarEl.scrollTop;
                let lastMovement = performance.now();

                // Also handle unchanged positions and browsers without scrollend.
                const waitForScroll = (now: number) => {
                    if (calendarEl.scrollLeft !== left || calendarEl.scrollTop !== top) {
                        left = calendarEl.scrollLeft;
                        top = calendarEl.scrollTop;
                        lastMovement = now;
                    }

                    if (now - lastMovement >= 150) {
                        highlight();
                    } else {
                        frame = requestAnimationFrame(waitForScroll);
                    }
                };

                frame = requestAnimationFrame(waitForScroll);
            } else {
                highlight();
            }
        }
    }, [est_start_time, est_finish_time])

    return <div
        className={styles.opStatus}
        title={`#${operation_code}`}
        style={{ background }}
        onClick={() => handleClick(id)}
    >
        {name}
    </div>
}
